import { useCallback, useEffect } from 'react';

import { debounce } from '@/utils/debounce.util';

export const useScroll = (
  parentRef: { current: HTMLElement | null } | HTMLElement | null,
  fetchNextPage?: () => void,
  hasNextPage?: boolean
) => {
  // Extract element from ref or use element directly
  const getElement = useCallback(() => {
    if (!parentRef) {
      return null;
    }

    const element = 'current' in parentRef ? parentRef.current : parentRef;
    return element;
  }, [parentRef]);

  const handleScroll = useCallback(() => {
    const element = getElement();

    if (element) {
      const { scrollTop, clientHeight, scrollHeight } = element;

      // Only trigger fetchNextPage if there's actually a next page to fetch
      // This prevents the "dead zone" at the bottom of the last page
      if (
        hasNextPage !== false &&
        scrollTop + clientHeight >= scrollHeight - 200
      ) {
        fetchNextPage?.();
      }
    }
  }, [getElement, fetchNextPage, hasNextPage]);

  useEffect(() => {
    const element = getElement();

    if (!element) {
      return;
    }

    const debouncedHandler = debounce(handleScroll, 500);

    element.addEventListener('scroll', debouncedHandler);

    return () => {
      debouncedHandler.cancel();
      element.removeEventListener('scroll', debouncedHandler);
    };
  }, [getElement, handleScroll]);
};
